import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";

const STORAGE_KEY = "bday-cookie-banner-dismissed";

// Visual port of the old MUI Snackbar cookie banner. Anchored bottom-center,
// 800px wide with `calc(100% - 18px)` clamp; white background, #2b2b2b text;
// links #17a17e underlined; mobile floats the 🍪 emoji above the message.
export function CookieBanner() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      setDismissed(false);
    }
    setMounted(true);
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore (private mode / disabled storage)
    }
    setDismissed(true);
  };

  if (dismissed) return null;
  if (user) return null;
  if (pathname.startsWith("/admin")) return null;
  if (pathname.startsWith("/card/read/") || pathname.startsWith("/card/sign/")) return null;

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
      className={`fixed bottom-2 left-1/2 z-50 w-[800px] max-w-[calc(100%-18px)] -translate-x-1/2 transition-transform duration-200 ${
        mounted ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="flex items-center justify-between rounded-[4px] bg-white px-4 py-[6px] text-foreground shadow-[0_3px_5px_-1px_rgba(0,0,0,0.2),0_6px_10px_0_rgba(0,0,0,0.14),0_1px_18px_0_rgba(0,0,0,0.12)]">
        <div className={`relative flex items-center ${isMobile ? "text-xs" : "text-sm"}`}>
          {isMobile ? (
            <span aria-hidden className="absolute -top-9 -left-6 text-[32px] leading-none">
              🍪
            </span>
          ) : (
            <span aria-hidden className="mr-4 text-[32px] leading-none">
              🍪
            </span>
          )}
          <div>
            <span>
              We use necessary cookies to make our site work and to anonymously analyze usage. By
              using our site, you agree to our
            </span>
            <Link to="/privacy" className="ml-[3px] cursor-pointer text-primary underline">
              Privacy Policy
            </Link>
            <span> and </span>
            <Link to="/terms" className="cursor-pointer text-primary underline">
              Terms of Service
            </Link>
          </div>
        </div>
        <button
          type="button"
          onClick={accept}
          data-testid="cookie-banner-accept"
          className="ml-3 inline-flex min-h-[38px] min-w-[55px] cursor-pointer items-center justify-center rounded-[6px] bg-primary-hover px-2 py-1 text-sm font-bold uppercase text-white hover:bg-primary"
        >
          OK
        </button>
      </div>
    </div>
  );
}
